<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="loading">
      <v-row class="mb-2">
        <v-col v-for="n in 4" :key="n" cols="6" md="3">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="image" height="260" class="mb-4" />
      <v-skeleton-loader type="list-item@3" />
    </template>

    <template v-else>
      <v-row no-gutters align="center" class="mb-3">
        <p class="text-overline grey--text text--darken-1 mb-0">
          {{ $t("finance_kpi_cards_label") }}
        </p>
        <v-spacer />
        <div style="max-width: 280px; width: 100%">
          <dropdown
            v-model="filterPeriod"
            :items="periodOptions"
            item-text="label"
            item-value="value"
            :label="$t('finance_date')"
            hide-details
          />
        </div>
      </v-row>
      <v-row class="mb-2">
        <v-col
          v-for="(kpi, index) in kpis"
          :key="kpi.label"
          cols="6"
          sm="4"
          md="3"
          class="kpi-col"
        >
          <v-card
            outlined
            class="pa-4 fill-height"
            :class="{ 'kpi-card--primary': index === 0 && currentBalance !== null }"
          >
            <div class="text-caption grey--text text--darken-1">{{ kpi.label }}</div>
            <div
              class="text-h6 font-weight-bold mt-1"
              :style="index === 0 ? balanceValueStyle : null"
            >
              {{ kpi.value }}
            </div>
            <div
              v-if="kpi.trend_percent !== null && kpi.trend_percent !== undefined"
              class="text-caption mt-1 font-weight-medium"
              :style="{ color: kpi.trend_percent >= 0 ? '#00b37e' : '#d9534f' }"
            >
              {{ kpi.trend_percent >= 0 ? "↑ +" : "↓ " }}{{ kpi.trend_percent }}%
              {{ $t("finance_trend_vs_previous") }}
            </div>
          </v-card>
        </v-col>
        <v-col v-if="kpis.length === 0" cols="12">
          <empty-state icon="mdi-chart-box-outline" :title="$t('finance_no_kpi_data')" />
        </v-col>
      </v-row>

      <v-card
        v-if="moneyFlowSummary"
        outlined
        class="pa-4 mb-2"
      >
        <div class="text-subtitle-2 font-weight-bold mb-2">
          {{ $t("finance_activity_in_period") }}
        </div>
        <div class="mf-summary">
          <div v-for="row in moneyFlowRows" :key="row.labelKey" class="mf-summary-row">
            <span class="mf-dot" :style="{ background: row.color }" />
            <span class="mf-summary-label">{{ $t(row.labelKey) }}</span>
            <span class="mf-summary-value">{{ formatCurrency(row.value) }}</span>
          </div>
        </div>
      </v-card>

      <p class="text-overline grey--text text--darken-1 mb-1 mt-4">
        {{ $t("finance_charts_label") }}
      </p>
      <v-row class="mb-2">
        <v-col cols="12" md="7">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-subtitle-2 font-weight-bold">
              {{ $t("finance_turnover_by_day_title") }}
            </div>
            <div class="text-caption grey--text mb-2">
              {{
                $t("finance_last_7_days_currency_hint", { currency: restaurantCurrency })
              }}
            </div>

            <apexchart
              v-if="dailyTurnover.length"
              type="bar"
              height="260"
              :options="dailyTurnoverOptions"
              :series="dailyTurnoverSeries"
            />
            <empty-state v-else icon="mdi-chart-bar" :title="$t('finance_no_data')" />
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-subtitle-2 font-weight-bold">
              {{ $t("finance_income_vs_costs_title") }}
            </div>
            <div class="text-caption grey--text mb-2">
              {{
                $t("finance_this_month_currency_hint", { currency: restaurantCurrency })
              }}
            </div>

            <div class="d-flex mb-3" style="gap: 16px">
              <span
                class="d-flex align-center text-caption font-weight-medium grey--text text--darken-2"
              >
                <span class="legend-dot income mr-1" /> {{ $t("finance_income") }}
              </span>
              <span
                class="d-flex align-center text-caption font-weight-medium grey--text text--darken-2"
              >
                <span class="legend-dot cost mr-1" /> {{ $t("finance_costs") }}
              </span>
            </div>

            <div v-if="incomeVsCosts.length" class="hbar-list">
              <div v-for="row in incomeVsCosts" :key="row.label" class="hbar-row">
                <span class="hbar-label">{{ row.label }}</span>
                <div class="hbar-track">
                  <div
                    class="hbar-fill"
                    :class="row.kind"
                    :style="{ width: hBarWidth(row.value) }"
                  />
                </div>
                <span class="hbar-value">{{ formatCurrency(row.value) }}</span>
              </div>
            </div>
            <empty-state v-else icon="mdi-scale-balance" :title="$t('finance_no_data')" />
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">
          {{ $t("finance_recent_activity_title") }}
        </div>

        <empty-state
          v-if="recentActivity.length === 0"
          icon="mdi-history"
          :title="$t('finance_no_recent_activity')"
        />
        <div v-else class="activity-list">
          <div v-for="activity in recentActivity" :key="activity.id" class="activity-row">
            <v-icon size="18" color="success">mdi-check-circle</v-icon>
            <span class="activity-title">{{ describeActivity(activity) }}</span>
            <span class="activity-time">{{ formatRelativeTime(activity.at) }}</span>
          </div>
        </div>
      </v-card>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import EmptyState from "@/components/global/EmptyState.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantFinanceService from "@/services/restaurantFinance";
import { MONEY_FLOW_META, TRANSACTION_TYPE_LABEL_KEYS, toDateParam } from "./constants";

export default {
  name: "FinanceTabOverview",

  components: { apexchart: VueApexCharts, EmptyState },

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    kpis: [],
    currentBalance: null,
    moneyFlowSummary: null,
    dailyTurnover: [],
    incomeVsCosts: [],
    recentActivity: [],
    filterPeriod: "30d",
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant && this.$store.state.restaurant.id;
    },
    periodOptions() {
      return [
        { value: "7d", label: this.$t("finance_last_7_days") },
        { value: "30d", label: this.$t("finance_last_30_days") },
        { value: "90d", label: this.$t("finance_last_90_days") },
      ];
    },
    balanceValueStyle() {
      if (this.currentBalance === null) return null;
      return { color: this.currentBalance >= 0 ? "#00b37e" : "#d9534f" };
    },
    // Rezime aktivnosti u periodu (nije trenutni dug). Boje po istoj šemi kao
    // Transakcije tab: crvena = plaćeno Orderi, zelena = primljeno, plava = direktno.
    moneyFlowRows() {
      const s = this.moneyFlowSummary || {};
      return [
        {
          labelKey: "finance_mf_paid_to_ordera",
          color: MONEY_FLOW_META.RESTAURANT_OWES_ORDERA.color,
          value: s.paid_to_ordera_this_period || 0,
        },
        {
          labelKey: "finance_mf_received_from_ordera",
          color: MONEY_FLOW_META.ORDERA_OWES_RESTAURANT.color,
          value: s.received_from_ordera_this_period || 0,
        },
        {
          labelKey: "finance_mf_direct_payment",
          color: MONEY_FLOW_META.DIRECT_PAYMENT.color,
          value: s.direct_payments_this_period || 0,
        },
      ];
    },
    maxIncomeVsCosts() {
      return Math.max(1, ...this.incomeVsCosts.map((r) => r.value));
    },
    dailyTurnoverOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "Roboto, sans-serif" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#00b37e"],
        dataLabels: { enabled: false },
        xaxis: { categories: this.dailyTurnover.map((p) => p.label) },
        yaxis: { labels: { formatter: (v) => this.formatCurrency(v) } },
        tooltip: { y: { formatter: (v) => this.formatCurrency(v) } },
        grid: { borderColor: "#f1f1f1" },
      };
    },
    dailyTurnoverSeries() {
      return [
        {
          name: this.$t("finance_turnover_series_label"),
          data: this.dailyTurnover.map((p) => p.value),
        },
      ];
    },
  },

  created() {
    this.load();
  },

  watch: {
    filterPeriod() {
      this.load();
    },
  },

  methods: {
    // Preset -> ?from=&to=. Default (30d) šalje bez parametara = backend default.
    // current_balance nije pogođen periodom (uvijek trenutno stanje).
    periodParams() {
      if (this.filterPeriod === "30d") return undefined;
      const days = { "7d": 7, "90d": 90 }[this.filterPeriod];
      const to = new Date();
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      return { from: toDateParam(from), to: toDateParam(to) };
    },

    async load() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const res = await restaurantFinanceService.getFinancialOverview(
          this.restaurantId,
          this.periodParams()
        );
        const data = (res.data && res.data.data) || {};
        this.kpis = data.kpis || [];
        this.currentBalance =
          data.current_balance != null ? Number(data.current_balance) : null;
        this.moneyFlowSummary = data.money_flow_summary || null;
        this.dailyTurnover = data.daily_turnover || [];
        this.incomeVsCosts = data.income_vs_costs || [];
        this.recentActivity = data.recent_activity || [];
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_overview_load_error"));
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      const formatted = Number(value || 0).toLocaleString("sr-RS", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${formatted} ${this.restaurantCurrency}`;
    },
    // Backend šalje title kao sirovi kod ("ORDER_REVENUE - 8.30 KM") - isti
    // kodovi kao Transakcije/Knjiga salda tabovi, pa prevodimo prefiks preko
    // iste mape (TRANSACTION_TYPE_LABEL_KEYS).
    describeActivity(activity) {
      const raw = activity.title || "";
      const separatorIndex = raw.indexOf(" - ");
      if (separatorIndex === -1) return raw;
      const code = raw.slice(0, separatorIndex);
      const rest = raw.slice(separatorIndex);
      const key = TRANSACTION_TYPE_LABEL_KEYS[code];
      return key ? `${this.$t(key)}${rest}` : raw;
    },
    hBarWidth(value) {
      return `${Math.max(4, (value / this.maxIncomeVsCosts) * 100)}%`;
    },
    formatRelativeTime(iso) {
      if (!iso) return "";
      const diffMs = Date.now() - new Date(iso).getTime();
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return this.$t("finance_time_just_now");
      if (minutes < 60) return this.$t("finance_time_minutes_ago", { minutes });
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return this.$t("finance_time_hours_ago", { hours });
      const days = Math.floor(hours / 24);
      return this.$t("finance_time_days_ago", { days });
    },
  },
};
</script>

<style scoped>
@media (min-width: 1264px) {
  .kpi-col {
    flex: 0 0 20% !important;
    max-width: 20% !important;
  }
}

.kpi-card--primary {
  border-color: #00b37e !important;
  background: #f2fbf7;
}

.mf-summary {
  display: grid;
  gap: 8px;
}

.mf-summary-row {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
}

.mf-summary-label {
  color: rgba(0, 0, 0, 0.7);
  font-weight: 600;
}

.mf-summary-value {
  font-weight: 700;
  white-space: nowrap;
}

.mf-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}

.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot.income {
  background: #00b37e;
}

.legend-dot.cost {
  background: #d9820f;
}

.hbar-list {
  display: grid;
  gap: 10px;
}

.hbar-row {
  display: grid;
  grid-template-columns: 78px 1fr auto;
  align-items: center;
  gap: 10px;
}

.hbar-label {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
}

.hbar-track {
  height: 10px;
  background: #f2f3f7;
  border-radius: 4px;
  overflow: hidden;
}

.hbar-fill {
  height: 100%;
  border-radius: 4px;
}

.hbar-fill.income {
  background: #00b37e;
}

.hbar-fill.cost {
  background: #d9820f;
}

.hbar-value {
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  text-align: right;
}

.activity-list {
  display: grid;
  gap: 2px;
}

.activity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #eceef2;
}

.activity-row:last-child {
  border-bottom: none;
}

.activity-title {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
}

.activity-time {
  font-size: 0.78rem;
  color: #9aa4b2;
}
</style>
